// Regroupement des invalidations (spec §4 règle 2) et limitation des
// rafraîchissements déclenchés par un sujet public (spec I4).

export interface Timers {
    setTimeout(fn: () => void, ms: number): unknown;
    clearTimeout(handle: unknown): void;
}

/** Résolus à l'appel : les faux minuteurs de vitest s'y appliquent. */
export const realTimers: Timers = {
    setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
    clearTimeout: (handle) => globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
};

/** Fenêtre de regroupement par clé de requête (spec §4 règle 2). */
export const COALESCE_WINDOW_MS = 300;
/**
 * Au-delà, une exécution non terminée (requête bloquée, ou en pause hors
 * ligne) libère sa clé : sans plafond, un fetch sans fin figerait la clé.
 */
export const COALESCE_MAX_IN_FLIGHT_MS = 30_000;
/** Au plus un rafraîchissement par intervalle pour un ping public (spec I4). */
export const PUBLIC_REFETCH_THROTTLE_MS = 5_000;

/** Une exécution de clé : synchrone, ou une promesse (invalidateQueries) suivie jusqu'à son issue. */
export type CoalescedRun = () => void | PromiseLike<unknown>;

export interface Coalescer {
    /**
     * Le premier appel d'une clé ouvre une fenêtre fixe ; les appels suivants y
     * sont absorbés (la dernière fonction gagne). La fenêtre ne s'allonge pas.
     * Si `run` rend une promesse, la clé reste occupée jusqu'à son issue
     * (succès ou échec) : jamais deux exécutions simultanées d'une même clé.
     * Un appel arrivé pendant l'exécution la marque « à refaire » ; à l'issue,
     * une nouvelle fenêtre s'ouvre et la dernière fonction reçue s'exécute une
     * fois. Tout appel postérieur au début d'une exécution obtient donc une
     * exécution ultérieure. Une exécution qui dépasse `maxInFlightMs` est
     * tenue pour terminée ; son issue tardive est ignorée.
     */
    schedule(key: string, run: CoalescedRun): void;
    cancelAll(): void;
}

const isThenable = (value: unknown): value is PromiseLike<unknown> =>
    typeof value === 'object' && value !== null && typeof (value as { then?: unknown }).then === 'function';

export function createCoalescer(
    windowMs: number = COALESCE_WINDOW_MS,
    timers: Timers = realTimers,
    maxInFlightMs: number = COALESCE_MAX_IN_FLIGHT_MS,
): Coalescer {
    interface Slot {
        run: CoalescedRun;
        /** Minuteur de fenêtre, ou du plafond pendant une exécution. */
        handle: unknown;
        inFlight: boolean;
        dirty: boolean;
        /** Numéro de l'exécution en cours : une issue d'une exécution antérieure est ignorée. */
        runId: number;
    }
    const slots = new Map<string, Slot>();

    const open = (key: string, slot: Slot) => {
        slot.handle = timers.setTimeout(() => fire(key, slot), windowMs);
    };

    // Issue de l'exécution : la clé se libère, ou repart pour une fenêtre si
    // un appel est arrivé entre-temps. Ignorée si la clé a été annulée.
    const settle = (key: string, slot: Slot, runId: number) => {
        if (slots.get(key) !== slot || !slot.inFlight || slot.runId !== runId) return;
        if (slot.handle !== null) timers.clearTimeout(slot.handle);
        slot.handle = null;
        slot.inFlight = false;
        if (slot.dirty) {
            slot.dirty = false;
            open(key, slot);
        } else {
            slots.delete(key);
        }
    };

    const fire = (key: string, slot: Slot) => {
        slot.handle = null;
        slot.inFlight = true;
        const runId = ++slot.runId;
        let result: void | PromiseLike<unknown>;
        try {
            result = slot.run();
        } catch (error) {
            settle(key, slot, runId);
            throw error;
        }
        if (isThenable(result)) {
            slot.handle = timers.setTimeout(() => settle(key, slot, runId), maxInFlightMs);
            // Échec absorbé ici : il ne bloque pas la clé et ne devient pas
            // une promesse rejetée non gérée.
            Promise.resolve(result).then(
                () => settle(key, slot, runId),
                () => settle(key, slot, runId),
            );
        } else {
            settle(key, slot, runId);
        }
    };

    return {
        schedule(key, run) {
            const slot = slots.get(key);
            if (slot) {
                slot.run = run;
                if (slot.inFlight) slot.dirty = true;
                return;
            }
            const created: Slot = { run, handle: null, inFlight: false, dirty: false, runId: 0 };
            slots.set(key, created);
            open(key, created);
        },
        cancelAll() {
            for (const slot of slots.values()) if (slot.handle !== null) timers.clearTimeout(slot.handle);
            slots.clear();
        },
    };
}

export interface Throttle {
    /** Exécute tout de suite si l'intervalle est libre ; sinon une seule exécution à sa fin. */
    run(fn: () => void): void;
    cancel(): void;
}

export function createThrottle(intervalMs: number = PUBLIC_REFETCH_THROTTLE_MS, timers: Timers = realTimers): Throttle {
    let handle: unknown = null;
    let trailing: (() => void) | null = null;
    const cool = () => {
        handle = timers.setTimeout(() => {
            handle = null;
            const next = trailing;
            trailing = null;
            if (next) {
                next();
                cool();
            }
        }, intervalMs);
    };
    return {
        run(fn) {
            if (handle !== null) {
                trailing = fn;
                return;
            }
            fn();
            cool();
        },
        cancel() {
            if (handle !== null) timers.clearTimeout(handle);
            handle = null;
            trailing = null;
        },
    };
}
